/**
 * formKit.js  —  Shared utilities for all Origo Health / Gazelle EHX forms
 *
 * Import in every form:
 *   import { STYLES, Field, YesNo, SelectLevel, CheckGroup,
 *            saveToMongo, downloadPDF, C } from '../shared/formKit';
 */

import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { API_PATHS, instance } from "../../../../services";
import { globalConfig } from "../../../../config";

// ─── Design tokens ────────────────────────────────────────────────────────────
export const C = {
  navy:   "#0F2B4C",
  teal:   "#0A8A7A",
  tealLt: "#E6F5F3",
  accent: "#D4A843",
  bg:     "#F7F9FC",
  white:  "#FFFFFF",
  border: "#CBD5E1",
  text:   "#1E293B",
  muted:  "#64748B",
  error:  "#DC2626",
  green:  "#166534",
  greenLt:"#F0FDF4",
};

// ─── Global CSS (inject once via <style> in each form root) ──────────────────
export const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .wf-root { font-family:'DM Sans',sans-serif; background:${C.bg}; color:${C.text}; min-height:100vh; padding:32px 16px 64px; }
  .wf-card { max-width:820px; margin:0 auto; background:${C.white}; border-radius:16px; box-shadow:0 4px 32px rgba(15,43,76,.10); overflow:hidden; }

  /* Header */
  .wf-header { background:${C.navy}; padding:36px 40px 28px; position:relative; overflow:hidden; }
  .wf-header::before { content:''; position:absolute; top:-40px; right:-40px; width:220px; height:220px; border-radius:50%; background:rgba(10,138,122,.18); }
  .wf-header-label { font-family:'DM Serif Display',serif; font-size:11px; letter-spacing:3px; text-transform:uppercase; color:${C.accent}; margin-bottom:8px; }
  .wf-header h1 { font-family:'DM Serif Display',serif; font-size:clamp(20px,4vw,30px); font-weight:400; color:${C.white}; line-height:1.2; }
  .wf-header p { margin-top:10px; font-size:13px; color:rgba(255,255,255,.65); max-width:560px; line-height:1.6; }
  .wf-db-badge { display:inline-flex; align-items:center; gap:6px; background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.20); border-radius:20px; padding:4px 12px; margin-top:14px; font-size:11px; font-weight:600; letter-spacing:.5px; color:rgba(255,255,255,.80); }
  .wf-db-badge .dot { width:7px; height:7px; border-radius:50%; background:#57A85A; }

  /* Patient bar */
  .wf-patient-bar { background:${C.tealLt}; border-bottom:1px solid ${C.border}; padding:16px 40px; display:flex; gap:32px; flex-wrap:wrap; }
  .wf-patient-field label { display:block; font-size:10px; letter-spacing:1.5px; text-transform:uppercase; color:${C.muted}; margin-bottom:4px; }
  .wf-patient-field span { font-size:15px; font-weight:600; color:${C.navy}; }

  /* Saved bar */
  .wf-saved-bar { background:${C.greenLt}; border-bottom:1px solid #BBF7D0; padding:10px 40px; display:flex; align-items:center; gap:10px; font-size:13px; color:${C.green}; }
  .wf-saved-bar code { background:#DCFCE7; border-radius:4px; padding:2px 8px; font-size:12px; font-family:monospace; color:#15803D; }

  /* Body */
  .wf-body { padding:32px 40px; }

  /* Sections */
  .wf-section { margin-bottom:32px; padding-bottom:28px; border-bottom:1px dashed ${C.border}; }
  .wf-section:last-child { border-bottom:none; }
  .wf-section-title { font-family:'DM Serif Display',serif; font-size:17px; font-weight:400; color:${C.navy}; margin-bottom:16px; display:flex; align-items:center; gap:10px; }
  .wf-section-title .num { background:${C.navy}; color:${C.white}; font-family:'DM Sans',sans-serif; font-size:11px; font-weight:700; width:24px; height:24px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; flex-shrink:0; }

  /* Policy text */
  .wf-policy { font-size:13px; line-height:1.75; color:${C.text}; background:#F8FAFC; border:1px solid ${C.border}; border-radius:10px; padding:20px 24px; margin-bottom:20px; }
  .wf-policy p { margin-bottom:12px; } .wf-policy p:last-child { margin-bottom:0; }
  .wf-policy ul { padding-left:20px; margin-top:8px; } .wf-policy li { margin-bottom:6px; }

  /* Grids */
  .wf-grid { display:grid; gap:14px 20px; }
  .wf-grid-2 { grid-template-columns:1fr 1fr; }
  .wf-grid-3 { grid-template-columns:1fr 1fr 1fr; }
  .wf-grid-4 { grid-template-columns:1fr 1fr 1fr 1fr; }

  /* Fields */
  .wf-field { display:flex; flex-direction:column; gap:5px; }
  .wf-field label { font-size:12px; font-weight:600; color:${C.muted}; letter-spacing:.3px; }
  .wf-field input[type="text"],.wf-field input[type="number"],.wf-field input[type="date"],.wf-field input[type="tel"],.wf-field input[type="email"],.wf-field select,.wf-field textarea {
    border:1.5px solid ${C.border}; border-radius:8px; padding:9px 12px;
    font-family:'DM Sans',sans-serif; font-size:14px; color:${C.text};
    background:${C.white}; transition:border-color .2s; outline:none; width:100%;
  }
  .wf-field input:focus,.wf-field select:focus,.wf-field textarea:focus { border-color:${C.teal}; box-shadow:0 0 0 3px rgba(10,138,122,.10); }
  .wf-field textarea { resize:vertical; min-height:72px; }

  /* Radios / Checks */
  .wf-radio-group,.wf-check-group { display:flex; flex-wrap:wrap; gap:8px; margin-top:2px; }
  .wf-radio-label,.wf-check-label { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border:1.5px solid ${C.border}; border-radius:6px; cursor:pointer; font-size:13px; font-weight:500; transition:all .15s; user-select:none; }
  .wf-radio-label:has(input:checked),.wf-check-label:has(input:checked) { border-color:${C.teal}; background:${C.tealLt}; color:${C.teal}; }
  .wf-radio-label input,.wf-check-label input { accent-color:${C.teal}; }

  /* Tri-state (Yes/Sometimes/No) */
  .wf-tri-group { display:flex; flex-wrap:wrap; gap:8px; }
  .wf-tri-label { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border:1.5px solid ${C.border}; border-radius:6px; cursor:pointer; font-size:12px; font-weight:500; transition:all .15s; user-select:none; }
  .wf-tri-label:has(input:checked) { border-color:${C.teal}; background:${C.tealLt}; color:${C.teal}; }
  .wf-tri-label input { accent-color:${C.teal}; }

  /* Signature */
  .wf-sig-box { border:1.5px dashed ${C.border}; border-radius:10px; background:#FAFBFC; overflow:hidden; position:relative; }
  .wf-sig-box canvas { display:block; width:100% !important; height:150px !important; }
  .wf-sig-actions { display:flex; justify-content:flex-end; gap:10px; padding:8px 12px; background:#F1F5F9; border-top:1px solid ${C.border}; }
  .wf-sig-hint { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); font-size:13px; color:#94A3B8; pointer-events:none; }

  /* Buttons */
  .btn { padding:9px 18px; border-radius:8px; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600; cursor:pointer; border:none; transition:all .18s; }
  .btn-ghost { background:transparent; border:1.5px solid ${C.border}; color:${C.muted}; }
  .btn-ghost:hover { border-color:${C.teal}; color:${C.teal}; }
  .btn-teal { background:${C.teal}; color:${C.white}; }
  .btn-teal:hover { background:#097A6C; }
  .btn-navy { background:${C.navy}; color:${C.white}; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  /* Footer */
  .wf-footer { border-top:1px solid ${C.border}; padding:24px 40px; display:flex; gap:12px; align-items:center; flex-wrap:wrap; background:#F8FAFC; }
  .wf-footer-note { flex:1; font-size:11px; color:${C.muted}; line-height:1.5; }

  /* Toast */
  .wf-toast { position:fixed; bottom:24px; right:24px; padding:14px 20px; border-radius:10px; font-size:14px; font-weight:500; color:${C.white}; background:${C.navy}; box-shadow:0 8px 24px rgba(0,0,0,.18); z-index:9999; animation:slideUp .3s ease; }
  .wf-toast.success { background:${C.teal}; }
  .wf-toast.error { background:${C.error}; }
  @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }

  /* Responsive */
  @media(max-width:600px){
    .wf-grid-2,.wf-grid-3,.wf-grid-4{grid-template-columns:1fr;}
    .wf-body{padding:20px 16px;}
    .wf-patient-bar,.wf-footer{padding:14px 16px;}
    .wf-header{padding:24px 16px 20px;}
  }
`;

// ─── Reusable field components ────────────────────────────────────────────────
export function Field({ label, children, style }) {
  return <div className="wf-field" style={style}><label>{label}</label>{children}</div>;
}

export function YesNo({ name, value, onChange, explain, explainValue, onExplainChange }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      <div className="wf-radio-group">
        {["Yes","No"].map(o => (
          <label className="wf-radio-label" key={o}>
            <input type="radio" name={name} value={o} checked={value===o} onChange={e=>onChange(e.target.value)} />{o}
          </label>
        ))}
      </div>
      {explain && value==="Yes" && (
        <input type="text" placeholder={explain} value={explainValue||""} onChange={e=>onExplainChange(e.target.value)} />
      )}
    </div>
  );
}

export function TriState({ name, value, onChange }) {
  return (
    <div className="wf-tri-group">
      {[["yes","Yes / Often"],["sometimes","Sometimes"],["no","No / Never"]].map(([v,l])=>(
        <label className="wf-tri-label" key={v}>
          <input type="radio" name={name} value={v} checked={value===v} onChange={e=>onChange(e.target.value)} />{l}
        </label>
      ))}
    </div>
  );
}

export function SelectLevel({ value, onChange, options }) {
  return (
    <div className="wf-radio-group">
      {options.map(o=>(
        <label className="wf-radio-label" key={o}>
          <input type="radio" value={o} checked={value===o} onChange={e=>onChange(e.target.value)} />{o}
        </label>
      ))}
    </div>
  );
}

export function CheckGroup({ options, values, onChange }) {
  // options: [[key, label], ...]   values: { key: bool }
  return (
    <div className="wf-check-group">
      {options.map(([k,lbl])=>(
        <label className="wf-check-label" key={k}>
          <input type="checkbox" checked={!!values[k]} onChange={e=>onChange(k,e.target.checked)} />{lbl}
        </label>
      ))}
    </div>
  );
}

// ─── Signature block component ────────────────────────────────────────────────
export function SigBlock({ sigRef, sigEmpty, setSigEmpty, responsibleParty, setResponsibleParty,
                           relationship, setRelationship, sigDate, setSigDate, showRelationship=true }) {
  return (
    <div className="wf-section" style={{borderBottom:"none"}}>
      <div className="wf-section-title"><span className="num">✍</span>Authorization &amp; Signature</div>
      <div className="wf-grid wf-grid-2" style={{marginBottom:20}}>
        <Field label="Name (Patient or Responsible Party)">
          <input type="text" value={responsibleParty} onChange={e=>setResponsibleParty(e.target.value)} placeholder="Full name" />
        </Field>
        {showRelationship && (
          <Field label="Relationship to Patient">
            <input type="text" value={relationship} onChange={e=>setRelationship(e.target.value)} placeholder="Self / Parent / Guardian…" />
          </Field>
        )}
        <Field label="Date">
          <input type="date" value={sigDate} onChange={e=>setSigDate(e.target.value)} />
        </Field>
      </div>
      <Field label="Signature (draw below)">
        <div className="wf-sig-box">
          {sigEmpty && <div className="wf-sig-hint">Sign here</div>}
          <SignatureCanvas ref={sigRef} penColor={C.navy}
            canvasProps={{height:150,style:{width:"100%"}}}
            onBegin={()=>setSigEmpty(false)} />
          <div className="wf-sig-actions">
            <button className="btn btn-ghost" type="button"
              onClick={()=>{sigRef.current?.clear();setSigEmpty(true);}}>Clear</button>
          </div>
        </div>
      </Field>
      <p style={{marginTop:12,fontSize:11,color:C.muted,lineHeight:1.5}}>
        Note: Patients are solely responsible for maintaining the privacy and security of all information printed from the Patient Portal.
      </p>
    </div>
  );
}

// ─── DB helper ────────────────────────────────────────────────────────────────
export async function saveToMongo(document, apiUrl) {
  const slug = String(apiUrl || '')
    .replace(/^\/api\/forms\//, '')
    .replace(/^api\/forms\//, '')
    .replace(/^\//, '');

  // include slug in the payload body and post to the canonical /submit endpoint
  const payload = {
    ...document,
    status: 'review',
    slug: String(slug),
  };

  const primaryEndpoint = `${API_PATHS.mainPath}/forms-service/submit`;

  try {
    const { data } = await instance.post(primaryEndpoint, payload);
    return data;
  } catch (error) {
    // Fallback to absolute security URL for environments with non-standard axios base routing.
    const fallbackUrl = `${globalConfig.apiMainBaseURL}/forms-service/submit`;
    // ensure slug is set (defensive)
    payload.slug = payload.slug || String(slug);
    const { data } = await instance.post(fallbackUrl, payload);
    return data;
  }
}

// ─── PDF helper ───────────────────────────────────────────────────────────────
export async function downloadPDF(formRef, filename) {
  const canvas = await html2canvas(formRef.current, { scale:2, useCORS:true, logging:false });
  const img = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const ih = pw * (canvas.height / canvas.width);
  let y=0, rem=ih;
  while(rem>0){ if(y>0)pdf.addPage(); pdf.addImage(img,"PNG",0,-y,pw,ih); y+=ph; rem-=ph; }
  pdf.save(filename);
}

// ─── Toast hook ───────────────────────────────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);
  const show = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };
  const Toast = toast ? <div className={`wf-toast ${toast.type}`}>{toast.msg}</div> : null;
  return [show, Toast];
}

// ─── Form shell ───────────────────────────────────────────────────────────────
export function FormShell({ title, subtitle, label="Origo Health · Clinical Documentation",
    dbLabel, patientName, patientAge, patientGender, savedId, formRef, children, onSubmit,
    onDownload, submitting }) {
  return (
    <>
      <style>{STYLES}</style>
      <div className="wf-root">
        <div className="wf-card" ref={formRef}>
          <div className="wf-header">
            <div className="wf-header-label">{label}</div>
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
            {dbLabel && <div className="wf-db-badge"><span className="dot"/>{dbLabel}</div>}
          </div>
          {(patientName||patientAge||patientGender) && (
            <div className="wf-patient-bar">
              {patientName  && <div className="wf-patient-field"><label>Patient Name</label><span>{patientName}</span></div>}
              {patientAge   && <div className="wf-patient-field"><label>Age</label><span>{patientAge}</span></div>}
              {patientGender&& <div className="wf-patient-field"><label>Gender</label><span>{patientGender}</span></div>}
            </div>
          )}
          {savedId && (
            <div className="wf-saved-bar">✅ Saved — MongoDB <code>_id: {savedId}</code></div>
          )}
          <div className="wf-body">{children}</div>
          <div className="wf-footer">
            <div className="wf-footer-note">All records are stored in MongoDB and encrypted in compliance with HIPAA regulations.</div>
            <button className="btn btn-ghost" type="button" onClick={onDownload}>⬇ Download PDF</button>
            <button className="btn btn-teal" type="button" onClick={onSubmit} disabled={submitting}>
              {submitting?"Saving…":"Submit Form for Review"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}